import { useState } from "react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { format, getDay, startOfDay } from "date-fns";
import { MIN_AVG_MAX_KEYS } from "@repo/utils";
import { useEstimateStructure } from "@tools";
import { Grid, IconButton } from "@material-ui/core";
import {Dialog, Button, DatePicker, InfoLabel} from "@components";
import { TimelineContentSwitch } from "./components/TimelineContentSwitch";
import { Settings } from "@assets";
import classnames from "classnames";

export const ProjectTimelineSettings = observer(() => {
  const { t } = useTranslation();
  const structure = useEstimateStructure();
  const { settings } = structure;

  const [ dialogVisible, showDialog ] = useState(false);
  const [ datepickerAnchor, setDatepickerAnchor ] = useState(null);
  const [ datepickerVisible, showDatepicker ] = useState(false);
  const [ startDate, setStartDate ] = useState(
    settings.timelineStartDate || new Date(),
  );

  const handleDialogVisibility = () => {
    if (!dialogVisible) {
      setStartDate(settings.timelineStartDate || new Date());
      showDatepicker(false);
    }
    showDialog(!dialogVisible);
  };

  const handleDateCancel = () => {
    setStartDate(settings.timelineStartDate || new Date());
    showDatepicker(false);
  };

  const handleDateChange = (value) => {
    if (!value) return;
    setStartDate(value);
    showDatepicker(false);
  };

  const handleShowTimelineSections = () => {
    structure.historyManager.startGroup();
    try {
      settings.setUseTimelineSections(!settings.useTimelineSections);
      structure.generateTimeline();
    } finally {
      structure.historyManager.stopGroup();
    }
  };

  const handleShowTimelineUtilization = () => {
    settings.setUseTimelineUtilization(!settings.useTimelineUtilization);
  };

  const handleShowTimelineBilling = () => {
    settings.setUseTimelineBilling(!settings.useTimelineBilling);
  };

  const apply = () => {
    structure.historyManager.startGroup();
    try {
      MIN_AVG_MAX_KEYS
        .map((key) => structure.timelines?.[key])
        .filter(Boolean)
        .forEach((timeline) => {
          timeline.updateStartDates( structure.settings.timelineStartDate, startOfDay(startDate));
        });
      structure.settings.setTimelineStartDate(startOfDay(startDate));
      showDialog(false);
    } finally {
      structure.historyManager.stopGroup();
    }
  };

  return (
    <>
      <IconButton
        tabIndex={-1}
        size="small"
        color="primary"
        onClick={handleDialogVisibility}
        className="ml-2"
      >
        <Settings />
      </IconButton>
      <Dialog
        open={dialogVisible}
        onClose={handleDialogVisibility}
        title={t("views.editor.dialogs.timeline_settings.project_title")}
        width={460}
        actions={
          <Button variant="contained" color="primary" onClick={apply}>
            {t("common.apply")}
          </Button>
        }
      >
        <Grid container direction="column" spacing={2} className="p-4">
          <Grid item container direction="column">
            <Grid
              item
              container
              justifyContent="space-between"
              alignItems="center"
              wrap="nowrap"
              className="mb-4"
            >
              <InfoLabel label="views.editor.dialogs.timeline_settings.start_date" translate className="mb-0" />
              <Button
                ref={setDatepickerAnchor}
                variant="outlined"
                color="primary"
                className="w-208"
                onClick={() => showDatepicker(!datepickerVisible)}
              >
                {format(startDate, "dd MMM yyyy")}
              </Button>
              <DatePicker
                isOpen={datepickerVisible}
                anchorEl={datepickerAnchor}
                value={startDate}
                changeHandler={handleDateChange}
                cancelHandler={handleDateCancel}
                shouldDisableDate={(value) => [0, 6].includes(getDay(value))}
              />
            </Grid>
            <TimelineContentSwitch
              title="proposal_parts.timeline_dropdowns.sections"
              onChange={handleShowTimelineSections}
              value={settings.useTimelineSections}
            />
            <TimelineContentSwitch
              title="proposal_parts.timeline_dropdowns.utilization"
              onChange={handleShowTimelineUtilization}
              value={settings.useTimelineUtilization}
            />
            <TimelineContentSwitch
              title="proposal_parts.timeline_dropdowns.billing"
              onChange={handleShowTimelineBilling}
              value={settings.useTimelineBilling}
              isLast
            />
          </Grid>
        </Grid>
      </Dialog>
    </>
  );
});

ProjectTimelineSettings.displayName = "ProjectTimelineSettings";
